import React, { useState } from "react";

import classNames from "classnames";

import { useTranslation } from "components/i18n/source";
import { Button, Dropdown, Header, Text, useMedia } from "components/ui/source";
import { SlideUpModal } from "components/ui/source/components/SlideUpModal/SlideUpModal";

import { IconStarFilled } from "icons";

import styles from "./Rating.module.scss";

export enum RatingState {
  ENABLED = "ENABLED",
  NEW_TO_SONDER = "NEW_TO_SONDER",
  PENDING_REVIEW = "PENDING_REVIEW",
  PENDING = "PENDING",
}

export type RatingProps = {
  allowClick?: boolean;
  average?: number | null;
  className?: string;
  onOpen?: () => void;
  showRatingPhrase?: boolean;
  state?: RatingState;
  total?: number | null;
};

const convertAverageToRatingPhrase = (average: number) =>
  (average > 4.69 && "components.rating.ratingA") ||
  (average > 4.39 && "components.rating.ratingB") ||
  (average > 4.09 && "components.rating.ratingC") ||
  (average > 3.79 && "components.rating.ratingD") ||
  (average > 3.49 && "components.rating.ratingE") ||
  null;

const MAX_RATING = 5;

export const Rating = ({
  allowClick = true,
  average = 0,
  className = "",
  onOpen,
  showRatingPhrase = true,
  state,
  total = 0,
}: RatingProps) => {
  const { t } = useTranslation();
  const isMobile = useMedia(["$tablet"], [true], false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Cap average to 0 or MAX_RATING, and fix to 1 decimal place.
  // - `toFixed(1)` will return 4.0 for 4, which is undesirable.
  // - `toFixed(1)` will round 3.86 to 3.9, which may be undesirable as it is slightly misleading.
  const cappedAverage = Math.min(Math.max(0, average || 0), MAX_RATING);
  const fixedAverage = Math.floor(cappedAverage * 10) / 10;

  const ratingPhraseKey = convertAverageToRatingPhrase(fixedAverage);
  const ratingPhraseCopy = ratingPhraseKey ? t(ratingPhraseKey) : null;

  const isEmpty = !total;
  const isDisabled = !state;
  const isEnabled = state === RatingState.ENABLED;
  const isNew = state === RatingState.NEW_TO_SONDER;
  const isPending =
    state === RatingState.PENDING || state === RatingState.PENDING_REVIEW;

  const iconColor = (isEnabled && !isEmpty) || isNew ? "marigold" : "gray";

  const emptyCopy =
    (isNew && t("components.rating.copyNew")) ||
    (isPending && t("components.rating.copyPending")) ||
    (isEnabled && isEmpty && t("components.rating.copyEmpty")) ||
    (isDisabled && t("components.rating.copyEmpty")) ||
    null;

  // Cap Total Reviews to 999.
  // Localize with plurality if <= 999;
  const isMaxTotal = (total || 0) > 999;
  const totalReviewsCopy = isMaxTotal
    ? t("components.rating.maxTotal")
    : t("components.rating.total", { count: total || 0 });

  const pendingClass =
    isPending || (isEnabled && isEmpty) ? styles.isPending : null;

  // Pivot between Simple Dropdown and Simple Modal depending on isMobile flag.
  const responsivePopup = isMobile ? (
    <>
      <SlideUpModal
        className={styles.modal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <Header
          size="md"
          translateKey="components.rating.title"
          type="headline3"
        />
        <Text
          className={styles.modalDescription}
          translateKey="components.rating.description"
          types={["body1"]}
        />
        <Button
          onClick={() => {
            setIsModalOpen(false);
          }}
          upperCase={false}
        >
          {t("components.rating.cta")}
        </Button>
      </SlideUpModal>
      <Button
        onClick={e => {
          if (onOpen) onOpen();
          setIsModalOpen(true);
          e.preventDefault();
          e.stopPropagation();
        }}
        upperCase={false}
        type="no_background"
      >
        {totalReviewsCopy}
      </Button>
    </>
  ) : (
    <Dropdown
      buttonType="no_background"
      className={styles.dropdown}
      label={<>{totalReviewsCopy}</>}
      noIcon
      onClick={(e: React.MouseEvent) => {
        if (onOpen) onOpen();
        e.preventDefault();
        e.stopPropagation();
      }}
      outlineOnFocus={false}
      width="fit-content"
    >
      <div className={styles.dropdownWrapper}>
        <Text element="b">{t("components.rating.title")}</Text>
        <Text element="div">{t("components.rating.description")}</Text>
      </div>
    </Dropdown>
  );

  return (
    <div className={classNames(styles.rating, pendingClass, className)}>
      <IconStarFilled color={iconColor} />
      {isEnabled && !isEmpty && (
        <>
          <b>{fixedAverage}</b>/{MAX_RATING}&nbsp;
          {showRatingPhrase && ratingPhraseCopy && (
            <>
              <span>{ratingPhraseCopy}</span>&nbsp;
            </>
          )}
          ({allowClick ? responsivePopup : <span>{totalReviewsCopy}</span>})
        </>
      )}
      {emptyCopy}
    </div>
  );
};
